import React, { createContext, useEffect, useState, useCallback } from 'react'
import { useSnapshot } from 'valtio'
import { UserState } from '@app/storage/user'
import { checkFeatureFlags } from '@app/services/featureFlags'
import { FeatureFlagStatesType } from '@app/services/featureFlags/types'
import { FeatureFlagContextType } from './types'

export const FeatureFlagContext = createContext<FeatureFlagContextType>({
  featureFlags: {},
  featureFlagsLoading: false,
})

type Props = {
  children: React.ReactNode
}

export const FeatureFlagProvider: React.FC<Props> = ({ children }) => {
  const { user } = useSnapshot(UserState)

  const [enabled, setEnabled] = useState<FeatureFlagStatesType>({})
  const [loading, setLoading] = useState<boolean>(true)

  const loadFeatureFlags = useCallback(async () => {
    setLoading(true)

    try {
      // Use user email as userId strategy if user is available
      const strategies = user?.id ? { userId: user.id } : undefined

      const featureFlagsState = await checkFeatureFlags(strategies)

      setEnabled(featureFlagsState || {})
    } catch (error) {
      console.warn('[FeatureFlagProvider] Failed to load feature flags:', error)
      // Set empty object on error to allow app to continue
      setEnabled({})
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  useEffect(() => {
    let isMounted = true

    // Load feature flags when component mounts or user changes
    loadFeatureFlags().then(() => {
      if (!isMounted) {
        return
      }
    })

    return () => {
      isMounted = false
    }
  }, [loadFeatureFlags])

  return (
    <FeatureFlagContext.Provider
      value={{
        featureFlags: enabled,
        featureFlagsLoading: loading,
      }}
    >
      {children}
    </FeatureFlagContext.Provider>
  )
}

